/**
 * One descriptor per field type, and one registry the wizard, the schema and
 * the admin read path all look a field up in.
 *
 * A field's `type` used to be a string literal branched on in eight places
 * across four files. Adding a widget meant finding all eight, and missing one
 * failed silently: the answer stored, and the admin recap printed a blank.
 * Here the registry is a `Record` over the type union, so a new type is a
 * compile error naming every half that still has to be written.
 *
 * This is the half of a widget with no DOM in it. `lib/audition/steps.ts` is
 * imported by `api/_trpc/routers/audition.ts`, which is the single serverless
 * function serving every tRPC route, so nothing reachable from here may pull
 * in React. The drawing half is `components/audition/widgets.tsx`, a registry
 * of the same shape over the same union — forget either and the compiler says
 * which.
 *
 * WHERE A DESCRIPTOR LIVES. A widget with rules of its own owns its
 * descriptor in its own module, beside those rules — `timelineWidget` in
 * `job-timeline.ts`, `prioritiesWidget` in `job-priorities.ts`,
 * `ladderWidget` in `level-ladder.ts`. What is left here is the two whose
 * whole definition is their stored shape: a written string, and a pick from a
 * declared list. The shape builders they share live one module further down,
 * in `answer-shape.ts`, so a widget that owns its descriptor can reach them
 * without importing the registry it is a member of.
 */
import type { z } from 'zod'
import { asList, asText, optionSchema, textSchema } from './answer-shape.js'
import { prioritiesWidget } from './job-priorities.js'
import { timelineWidget } from './job-timeline.js'
import { ladderWidget } from './level-ladder.js'
import type { AuditionAnswer, AuditionField } from './steps.js'

/**
 * Every kind of question the wizard can ask.
 *
 * Read off the field union rather than written out a second time: both
 * registries are keyed by this, so adding an arm to `AuditionField` is
 * immediately a compile error in each half naming what the new widget still
 * owes the wizard. A hand-kept copy here would let an arm be added with no
 * widget behind it, which is the failure this whole file exists to end.
 */
export type AuditionFieldType = AuditionField['type']

/**
 * What a widget gets to look at besides its own stored answer — the same
 * facts whether it is settling or drawing.
 *
 * Two widgets need more than their own value: the ladder's open rungs depend
 * on the level answered beside it, and the timeline is laid out against the
 * month the applicant is in. Both are step-level facts rather than anything
 * the widget could fetch for itself.
 */
export interface StepContext {
  field: AuditionField
  /**
   * ONLY what was asked before this field: every finished step in full, then
   * this step's own earlier fields already settled, flat by field name. This
   * comment owns that rule; the producers (`answersBeforeStep` for the
   * finished steps, the step component for the layering) point back here.
   *
   * Finished steps cannot change under a widget, so they are as safe to read
   * as an earlier field on this one — and the ladder on Goals needs the level
   * from Background. Later fields are a different matter: settling is one
   * pass down `step.fields`, so a later field still holds whatever was stored
   * rather than what is about to be drawn, and reading one would settle
   * against a value the same pass is about to replace. The bag simply does
   * not contain those fields, so a widget reaching for a later one reads
   * `undefined`, which is the "not said yet" path every widget already has.
   *
   * `| undefined` said out loud, because this repo does not set
   * `noUncheckedIndexedAccess`: without it a `Record<string, AuditionAnswer>`
   * types every read as present, and the keys this bag deliberately omits
   * would come back as `AuditionAnswer` to the checker. A widget reaching
   * forward would compile clean and throw. The whole point of the omission is
   * that the compiler makes the reader handle it.
   */
  answers: Record<string, AuditionAnswer | undefined>
  /**
   * The day the wizard is being filled in on, fixed for the whole step.
   *
   * The one date. Everything that needs a line of months — drawing it, `ready`
   * judging an answer against it — builds that line from this with the same
   * pure function, so they hold the same line by construction. A line index
   * only means something next to the line it was taken from, and two clocks
   * read either side of a month boundary would draw two lines a month apart:
   * a stretch drawn right there on screen could hold Next with nothing saying
   * why.
   *
   * A date and not a line for the same reason. A line is derived, and handing
   * one around lets two callers hold different ones.
   */
  today: Date
}

/**
 * How one field validates.
 *
 * Both parameters named on purpose, the same reason `auditionAnswerSchema`
 * names both: `ZodType` in zod 4 is `ZodType<Output = unknown, Input =
 * unknown>`, so annotating the output alone erases the INPUT to `unknown` —
 * and that erasure spreads, turning the object `stepSchema` builds into one
 * accepting `Record<string, unknown>`. `undefined` is in the input because
 * an optional field's schema is what transforms a missing key into its empty
 * value.
 */
export type StoredSchema = z.ZodType<AuditionAnswer, AuditionAnswer | undefined>

/**
 * Everything the wizard, the schema and the read path need to know about one
 * kind of question.
 *
 * Deliberately untyped in the answer's own shape: the registry is one
 * `Record` over every field type, so a per-widget value parameter would
 * collapse to `AuditionAnswer` the moment it was looked up. Each descriptor
 * re-narrows with its own guard instead, which costs a line and carries no
 * unverified claim.
 *
 * WHAT A WIDGET CANNOT DRAW. A stored answer can hold something its widget
 * can no longer show: a timeline stretch whose months have left the line.
 * `settle` keeps it. Settling runs before anything is drawn, so erasing it
 * there loses an answer the applicant gave with nothing on screen to say so.
 * The widget draws it as unanswered and says why, and `ready` holds Next
 * until it is said again. A widget with nothing like that settles everything
 * into shape and is always ready.
 *
 * `ready` stays apart from `storedSchema` because it may read `ctx.today`,
 * and the schema is the server's too. A server clock a timezone away from the
 * applicant would refuse, on the first of every month, a plan they can see
 * drawn.
 */
export interface WidgetDescriptor {
  /** What a field of this type stores before the applicant touches it. */
  empty(field: AuditionField): AuditionAnswer
  /**
   * Whether a stored answer is a shape this widget can read.
   *
   * False is not an error: a question that used to be a chip group has rows
   * holding a plain string, and that is still what the applicant told us.
   * `fieldDisplayParts` is where the tolerance lives.
   */
  is(value: unknown): boolean
  /**
   * The stored answer brought back into agreement with what will be drawn,
   * short of erasing what this widget cannot draw (see the interface).
   *
   * Must be idempotent — it runs on the seeded state and after every edit —
   * and must answer this widget's own stored shape for ANY input, `undefined`
   * and a disagreeing shape included. A widget that passes a foreign shape
   * through hands the drawing half something it will coerce to blank, and the
   * applicant then submits the blank they never typed.
   */
  settle(value: AuditionAnswer | undefined, ctx: StepContext): AuditionAnswer
  /** How one field of this type validates, required-ness included. */
  storedSchema(field: AuditionField): StoredSchema
  /**
   * Whether a settled answer can go through Next as it stands: false only
   * while it holds something this widget cannot draw against `ctx` (see the
   * interface, which also says why this is not part of `storedSchema`).
   */
  ready(value: AuditionAnswer | undefined, ctx: StepContext): boolean
  /**
   * The answer as the pieces it should be shown as, in the field's own
   * vocabulary. Called only for a value this widget said it can read.
   */
  summary(value: AuditionAnswer | undefined, field: AuditionField): string[]
  /**
   * The answer as the one short thing worth scanning where a whole row has
   * to fit on a line — the queue's lens lines (LG-1052). Optional: a widget
   * whose summary is already that short declares none, and `fieldLensText`
   * reads its summary instead. Called only for a value this widget said it
   * can read. Not the field's `glance`, which is a label on the detail
   * page's facts card.
   */
  lensText?(value: AuditionAnswer | undefined, field: AuditionField): string
  /** Whether this field stores an array rather than a bare string. */
  list(field: AuditionField): boolean
  /** Whether it holds something the applicant WROTE. */
  prose: boolean
}

/**
 * Whether a chip group takes more than one answer.
 *
 * Named once and used by both the descriptor's `list` and its schema, so the
 * two can never answer differently for the same field.
 */
const chipsAreMulti = (field: AuditionField): boolean => field.multi === true

/** A written answer: stored as itself, settled to a string, shown as itself. */
const proseWidget: WidgetDescriptor = {
  empty: () => '',
  is: (value) => typeof value === 'string',
  settle: (value) => asText(value),
  storedSchema: textSchema,
  ready: () => true,
  summary: (value) =>
    typeof value === 'string' && value !== '' ? [value] : [],
  list: () => false,
  prose: true,
}

/** A pick from a declared list: one answer, or many. */
const chipsWidget: WidgetDescriptor = {
  empty: (field) => (chipsAreMulti(field) ? [] : ''),
  // Either shape, because a single group stores a string and a multi group
  // stores a list, and both are answers this widget can read.
  is: (value) => typeof value === 'string' || Array.isArray(value),
  // A group that gained or lost `multi` has drafts stored under the other
  // shape. Coerced rather than passed through: the drawing half reads one
  // shape only, and would show a list-shaped answer as nothing selected.
  settle: (value, ctx) =>
    chipsAreMulti(ctx.field) ? asList(value) : asText(value),
  storedSchema: (field) => optionSchema(field, chipsAreMulti(field)),
  ready: () => true,
  summary: (value) => {
    if (Array.isArray(value)) return value
    return typeof value === 'string' && value !== '' ? [value] : []
  },
  list: chipsAreMulti,
  prose: false,
}

export const WIDGETS: Record<AuditionFieldType, WidgetDescriptor> = {
  text: proseWidget,
  textarea: proseWidget,
  chips: chipsWidget,
  ladder: ladderWidget,
  timeline: timelineWidget,
  priorities: prioritiesWidget,
}

/**
 * Whether a field stores an array rather than a bare string.
 *
 * The one answer to that question, wherever it is asked.
 */
export function storesList(field: AuditionField): boolean {
  return WIDGETS[field.type].list(field)
}

/**
 * Whether a field holds something the applicant WROTE, rather than something
 * they picked or drew.
 *
 * Stated positively on purpose. The admin lens quote asks "is there prose
 * here to pull out?", and the old form of that question — "is this not a chip
 * group?" — answered yes for every widget nobody had invented yet. A new
 * field type is now silently excluded, which is the safe direction: the cost
 * of missing a quote is a blank pull-quote, the cost of a false positive is
 * `[object Object]` shown large to a reviewer.
 */
export function isProseField(field: AuditionField): boolean {
  return WIDGETS[field.type].prose
}
